import React, { useState, useEffect } from 'react';
import { getAuth, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject, uploadBytesResumable } from 'firebase/storage';
import { getFirestore, doc, updateDoc, arrayUnion, arrayRemove, getDoc, setDoc } from 'firebase/firestore';
import './styles/Admin.css';
import SEOHead from '../components/SEOHead';
import { db, auth } from '../firebase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faTrash, faSignOutAlt, faImages, faTimes, faSpinner, faEdit } from '@fortawesome/free-solid-svg-icons';

function Admin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [serviceImages, setServiceImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showCaptionModal, setShowCaptionModal] = useState(false);
  const [currentEditImage, setCurrentEditImage] = useState(null);
  const [tempCaption, setTempCaption] = useState('');
  const [focusPoint, setFocusPoint] = useState({ x: 50, y: 50 }); // Default center

  const services = [
    'jordvarme',
    'belaegning',
    'midlertidig-vej',
    'fundamenter',
    'kloak',
    'tilbygning'
  ];

  useEffect(() => {
    if (selectedService && isLoggedIn) {
      fetchServiceImages();
    }
  }, [selectedService]);

  const fetchServiceImages = async () => {
    try {
      const serviceDoc = await getDoc(doc(db, 'services', selectedService));
      if (serviceDoc.exists()) {
        setServiceImages(serviceDoc.data().images || []);
      }
    } catch (error) {
      setError('Fejl ved hentning af billeder: ' + error.message);
    }
  };

  const handleDeleteImage = async (image) => {
    if (!window.confirm('Er du sikker på, at du vil slette dette billede?')) {
      return;
    }

    try {
      // Slet fra Storage
      const storage = getStorage();
      const imageRef = ref(storage, image.url);
      await deleteObject(imageRef);

      // Slet fra Firestore
      const serviceRef = doc(db, 'services', selectedService);
      await updateDoc(serviceRef, {
        images: arrayRemove(image)
      });

      // Opdater lokalt state
      setServiceImages(prevImages => prevImages.filter(img => img.url !== image.url));
      setSuccess('Billede slettet succesfuldt');
    } catch (error) {
      setError('Fejl ved sletning af billede: ' + error.message);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    try {
      await signInWithEmailAndPassword(auth, email, password);
      setIsLoggedIn(true);
      setError('');
    } catch (error) {
      setError('Forkert email eller adgangskode');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0 || !selectedService) {
      setError('Vælg venligst både en service og mindst ét billede');
      return;
    }

    try {
      setIsLoading(true);
      setUploadProgress(0);
      const storage = getStorage();
      const uploadedImages = [];
      
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        
        setCurrentEditImage({
          file: file,
          previewUrl: URL.createObjectURL(file)
        });
        setShowCaptionModal(true);
        
        const caption = await new Promise((resolve) => {
          window.captionResolve = resolve;
        });
        
        const fileName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
        const storageRef = ref(storage, `${selectedService}/${fileName}`);
        const uploadTask = uploadBytesResumable(storageRef, file);
        
        uploadTask.on('state_changed', 
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(progress);
          }
        );

        await uploadTask;
        const downloadURL = await getDownloadURL(storageRef);
        
        uploadedImages.push({
          url: downloadURL,
          caption: caption.caption,
          focusPoint: caption.focusPoint,
          fileName: fileName,
          uploadDate: new Date().toISOString()
        });
      }

      const serviceRef = doc(db, 'services', selectedService);
      const serviceDoc = await getDoc(serviceRef);
      
      if (serviceDoc.exists()) {
        const existingImages = serviceDoc.data().images || [];
        await updateDoc(serviceRef, {
          images: [...existingImages, ...uploadedImages]
        });
      } else {
        await setDoc(serviceRef, {
          images: uploadedImages
        });
      }

      setSuccess(`${selectedFiles.length} billede(r) uploadet succesfuldt!`);
      resetUploadState();
      await fetchServiceImages();
      
    } catch (error) {
      console.error('Upload error:', error);
      setError('Fejl ved upload af billeder: ' + error.message);
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
      setShowCaptionModal(false);
      setCurrentEditImage(null);
    }
  };

  const resetUploadState = () => {
    setSelectedFiles([]);
    setPreviewImages([]);
    setUploadProgress(0);
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) fileInput.value = '';
  };

  const showCaptionDialog = (file) => {
    return new Promise((resolve) => {
      setCurrentEditImage({
        url: URL.createObjectURL(file),
        fileName: file.name
      });
      setTempCaption('');
      setShowCaptionModal(true);

      // Gem resolve funktionen så vi kan kalde den når modal lukkes
      window.captionResolve = resolve;
    });
  };

  // Håndter drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );
    if (files.length > 0) {
      setSelectedFiles(files);
      const previews = files.map(file => URL.createObjectURL(file));
      setPreviewImages(previews);
    }
  };

  // Håndter logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsLoggedIn(false);
      setEmail('');
      setPassword('');
    } catch (error) {
      setError('Fejl ved logout: ' + error.message);
    }
  };

  // Vis billedpreview
  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setShowImagePreview(true);
  };

  // Tilføj denne funktion til at håndtere fil input
  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files).filter(file => 
      file.type.startsWith('image/')
    );
    if (files.length > 0) {
      setSelectedFiles(files);
      const previews = files.map(file => URL.createObjectURL(file));
      setPreviewImages(previews);
    }
  };

  // Tilføj disse nye funktioner
  const handleCaptionSubmit = () => {
    if (window.captionResolve) {
      window.captionResolve({
        caption: tempCaption,
        focusPoint: focusPoint
      });
      window.captionResolve = null;
    }
    setShowCaptionModal(false);
    setCurrentEditImage(null);
    setTempCaption('');
    setFocusPoint({ x: 50, y: 50 }); // Reset focus point
  };

  const handleCaptionCancel = () => {
    if (window.captionResolve) {
      window.captionResolve('');
      window.captionResolve = null;
    }
    setShowCaptionModal(false);
    setCurrentEditImage(null);
    setTempCaption('');
  };

  // Opdater handleEditCaption funktionen
  const handleEditCaption = async (image) => {
    setCurrentEditImage(image);
    setTempCaption(image.caption || '');
    setShowCaptionModal(true);
  };

  const handleFocusPointChange = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setFocusPoint({ x, y });
  };

  if (!isLoggedIn) {
    return (
      <div className="admin-login">
        <SEOHead title="Admin | JBC Entreprenøren" description="Administration" url="/admin" noindex />
        <h1 className="admin-title">Admin Login</h1>
        {error && <p className="admin-error">{error}</p>}
        <form onSubmit={handleLogin} className="admin-form">
          <div className="admin-input-group">
            <label className="admin-input-label">Email</label>
            <input
              className="admin-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="admin-input-group">
            <label className="admin-input-label">Adgangskode</label>
            <input
              className="admin-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button className="admin-button" type="submit">Log ind</button>
        </form>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <SEOHead title="Admin | JBC Entreprenøren" description="Administration" url="/admin" noindex />
      <div className="admin-dashboard">
        <div className="admin-dashboard-header">
          <h1 className="admin-title">Admin Dashboard</h1>
          <button className="admin-button admin-logout-button" onClick={handleLogout}>
            <FontAwesomeIcon icon={faSignOutAlt} /> Log ud
          </button>
        </div>

        {error && <p className="admin-error">{error}</p>}
        {success && <p className="admin-success">{success}</p>}

        <div className="admin-service-selector">
          <select
            className="admin-select"
            value={selectedService}
            onChange={(e) => setSelectedService(e.target.value)}
          >
            <option value="">Vælg service</option>
            {services.map((service) => (
              <option key={service} value={service}>
                {service.charAt(0).toUpperCase() + service.slice(1).replace('-', ' ')}
              </option>
            ))}
          </select>
        </div>

        {selectedService && (
          <>
            <div
              className={`admin-upload-zone ${isDragging ? 'admin-dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById('fileInput').click()}
            >
              <input
                id="fileInput"
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileInputChange}
                className="admin-file-input"
                style={{ display: 'none' }}
              />
              <FontAwesomeIcon icon={faUpload} size="2x" className="admin-upload-icon" />
              <p>Træk og slip billeder her eller klik for at vælge filer</p>
            </div>

            {selectedFiles.length > 0 && (
              <div className="admin-upload-section">
                <div className="admin-preview-grid">
                  {Array.from(selectedFiles).map((file, index) => (
                    <div key={index} className="admin-preview-item">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Preview ${index + 1}`}
                        className="admin-preview-image"
                      />
                      <button
                        className="admin-remove-preview"
                        onClick={() => {
                          setSelectedFiles(prev => 
                            Array.from(prev).filter((_, i) => i !== index)
                          );
                        }}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  ))}
                </div>
                
                <button 
                  className="admin-button admin-upload-button"
                  onClick={handleFileUpload}
                  disabled={selectedFiles.length === 0}
                >
                  <FontAwesomeIcon icon={faUpload} /> Upload {selectedFiles.length} billede(r)
                </button>

                {uploadProgress > 0 && (
                  <div className="admin-progress-container">
                    <div 
                      className="admin-progress-bar"
                      style={{ width: `${uploadProgress}%` }}
                    />
                    <span className="admin-progress-text">{Math.round(uploadProgress)}%</span>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {selectedService && (
          <div className="admin-image-gallery">
            <h2 className="admin-subtitle">
              Uploadede billeder for {selectedService.charAt(0).toUpperCase() + selectedService.slice(1).replace('-', ' ')}
            </h2>
            <div className="admin-image-grid">
              {serviceImages.map((image, index) => (
                <div key={index} className="admin-image-item">
                  <div className="admin-image-wrapper" onClick={() => handleImageClick(image.url)}>
                    <img 
                      src={image.url} 
                      alt={image.caption || `${selectedService} ${index + 1}`}
                      className="admin-image"
                      loading="lazy"
                    />
                    <div className="admin-image-overlay">
                      <div className="admin-image-info">
                        <p>{image.caption || 'Ingen billedtekst'}</p>
                        <small>{new Date(image.uploadDate).toLocaleDateString()}</small>
                      </div>
                    </div>
                  </div>
                  <div className="admin-image-actions">
                    <button 
                      className="admin-edit-button"
                      onClick={() => handleEditCaption(image)}
                    >
                      <FontAwesomeIcon icon={faEdit} /> Rediger
                    </button>
                    <button 
                      className="admin-delete-button"
                      onClick={() => handleDeleteImage(image)}
                    >
                      <FontAwesomeIcon icon={faTrash} /> Slet
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {showImagePreview && selectedImage && (
          <div className="admin-modal" onClick={() => setShowImagePreview(false)}>
            <div className="admin-modal-content">
              <img src={selectedImage} alt="Preview" className="admin-modal-image" />
              <button className="admin-close-modal">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
        )}

        {showCaptionModal && currentEditImage && (
          <div className="admin-modal caption-modal">
            <div className="admin-modal-content caption-modal-content">
              <div className="caption-modal-image-container">
                <div 
                  className="focus-point-container"
                  onClick={handleFocusPointChange}
                >
                  <img 
                    src={currentEditImage.previewUrl || currentEditImage.url} 
                    alt="Preview" 
                    className="caption-modal-image"
                  />
                  <div 
                    className="focus-point"
                    style={{ 
                      left: `${focusPoint.x}%`, 
                      top: `${focusPoint.y}%` 
                    }}
                  />
                  <div className="focus-point-instructions">
                    Klik for at vælge fokuspunkt
                  </div>
                </div>
              </div>
              <div className="caption-modal-form">
                <h3>Tilføj billedtekst</h3>
                <textarea
                  className="admin-input caption-input"
                  value={tempCaption}
                  onChange={(e) => setTempCaption(e.target.value)}
                  placeholder="Skriv billedtekst her..."
                  autoFocus
                />
                <div className="caption-modal-buttons">
                  <button 
                    className="admin-button caption-submit"
                    onClick={handleCaptionSubmit}
                  >
                    Gem
                  </button>
                  <button 
                    className="admin-button caption-cancel"
                    onClick={handleCaptionCancel}
                  >
                    Annuller
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Admin;
